import { type ApiSession, type ApiUser } from '@/lib/api';
import { readSessionToken, writeSessionToken } from '@/lib/browser-session';

interface SessionSnapshot {
  revision: number;
  user: ApiUser | null;
  session: ApiSession | null;
  loading: boolean;
}

export class SupersededAuthOperation extends DOMException {
  constructor() {
    super('Operation was superseded by another session change.', 'AbortError');
  }
}

function ownedOperation(owns: () => boolean, signal?: AbortSignal) {
  const assertCurrent = () => {
    signal?.throwIfAborted();
    if (!owns()) throw new SupersededAuthOperation();
  };
  return {
    assertCurrent,
    isCurrent: () => !signal?.aborted && owns(),
    async run<T, R>(request: () => Promise<T>, apply: (result: T) => R): Promise<R> {
      assertCurrent();
      try {
        const result = await request();
        assertCurrent();
        return apply(result);
      } catch (error) {
        assertCurrent();
        throw error;
      }
    },
  };
}

export function createAuthOperations(snapshot: () => SessionSnapshot) {
  let authIntent = 0;
  let profileIntent = 0;
  let observedToken: string | null | undefined;
  return {
    storageChanged: () => readSessionToken() !== observedToken,
    invalidate: () => {
      authIntent += 1;
    },
    beginAuth() {
      const intent = ++authIntent;
      let token = readSessionToken();
      observedToken = token;
      const isLatest = () => intent === authIntent;
      const operation = ownedOperation(() => isLatest() && token === readSessionToken());
      return {
        ...operation,
        isLatest,
        get token() {
          return token;
        },
        setToken(next: string | null) {
          operation.assertCurrent();
          if (next !== token) writeSessionToken(next);
          token = observedToken = next;
        },
      };
    },
    beginProfile(revision: number, signal: AbortSignal) {
      signal.throwIfAborted();
      const { user, session, loading, revision: currentRevision } = snapshot();
      if (revision !== currentRevision) throw new SupersededAuthOperation();
      if (!user || !session || loading) throw new Error('Server sign-in is required to change your username.');
      const intent = authIntent;
      const profile = ++profileIntent;
      const operation = ownedOperation(
        () =>
          intent === authIntent &&
          profile === profileIntent &&
          revision === snapshot().revision &&
          session?.token === readSessionToken(),
        signal,
      );
      operation.assertCurrent();
      return { ...operation, user, token: session.token, signal };
    },
  };
}
